import type {
  LieferscheinAbschnitt,
  LieferscheinAntwort,
  LieferscheinVariante,
  LieferscheinZeile
} from '@/types/lieferschein';
import { ApiError } from '@/lib/api';

// Das Blatt des Glomer-Lieferscheins — gemeinsam für die Druckseite mit
// Sitzung (/bestellungen/…/lieferschein) und den iPhone-Druck in Safari
// (/drucken/…). Eine Stelle, damit beide Wege dasselbe Papier ergeben.

export const LIEFERSCHEIN_VARIANTEN: LieferscheinVariante[] = [
  'lieferschein',
  'offen',
  'letzte_sendung',
  'alle_versandt',
  'teillieferung'
];

export function lieferscheinFehlerText(err: unknown): string {
  const code = err instanceof ApiError ? err.code : 'unbekannt';
  switch (code) {
    case 'keine_shopify_bestellung':
      return 'Diese Bestellung kommt nicht aus dem Shop — dafür gibt es den Bestellzettel.';
    case 'shopify_nicht_erreichbar':
      return 'Shopify ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen oder über Order Printer drucken.';
    case 'shopify_zugang_abgewiesen':
    case 'shopify_nicht_konfiguriert':
      return 'Der Zugang zu Shopify fehlt oder wurde abgewiesen. Bis das behoben ist, bitte über Order Printer drucken.';
    case 'http_error':
      return 'Der Server hat nicht rechtzeitig geantwortet. Bitte gleich noch einmal versuchen oder über Order Printer drucken.';
    case 'shopify_bestellung_fehlt':
      return 'Shopify kennt diese Bestellung nicht (mehr).';
    default:
      return `Der Lieferschein konnte nicht geladen werden (${code}).`;
  }
}

export function LieferscheinBlatt({ daten: d, qrBild }: { daten: LieferscheinAntwort; qrBild: string }) {
  return (
    <div className="lieferschein">
      <div className="ls-kopf">
        {/* Umbruch nach dem Schrägstrich wie im Order-Printer-Druck —
            dort ergibt er sich aus der Spaltenbreite, hier steht er fest. */}
        <div className="ls-shop-titel">
          Glomer.com /<br />
          Buchhandlung Engel
        </div>
        {/* Wie in der Vorlage: Nummer und Datum rechtsbündig, der QR-Code
            darunter. Nur größer (Wunsch 28.10.2026). */}
        <div className="ls-kopf-rechts">
          <div className="ls-kopf-nummer">
            <div className="ls-kopf-label">Bestellung</div>
            <div className="ls-bestellnummer">{d.kopf.name}</div>
            {d.kopf.po_nummer && <div>Bestellnummer {d.kopf.po_nummer}</div>}
            {(d.kopf.datum_lang ?? d.kopf.datum) && <div>{d.kopf.datum_lang ?? d.kopf.datum}</div>}
          </div>
          {/* 35 mm: Der Code misst 41 Module mit Ruhezone, ein Modul ist
              damit 0,85 mm — sicher lesbar auf Armlänge. `pixelated`
              hält die Kanten scharf, wenn das 246-px-Bild hochskaliert. */}
          <img src={qrBild} alt={`QR-Code zu ${d.kopf.name}`} className="ls-qr" />
          {/* Wie auf dem B-Zettel: der Name direkt unter dem Code. Er
              schaut über den Buchrand hinaus (Wunsch 28.10.2026). */}
          {d.kunde && <div className="ls-kunde">{d.kunde}</div>}
        </div>
      </div>

      <div className="ls-adressen">
        <div className="ls-liefer">
          <p className="ls-absender">E&amp;V GmbH • Alexanderstr. 11 • 70184 Stuttgart</p>
          <p className="ls-adresse">
            {d.adressen.liefer ? <Zeilen zeilen={d.adressen.liefer} /> : 'Keine Lieferadresse'}
          </p>
        </div>
        <div className="ls-rechnung">
          <p className="ls-untertitel">Rechnung an</p>
          <p className="ls-adresse">
            {d.adressen.rechnung ? <Zeilen zeilen={d.adressen.rechnung} /> : 'Keine Rechnungsadresse'}
          </p>
        </div>
      </div>
      <hr className="ls-linie" />

      {d.hinweis !== null ? (
        <div className="ls-hinweis">
          <p>
            <strong>Hinweis:</strong> {d.hinweis}
          </p>
        </div>
      ) : (
        <table className="ls-tabelle">
          {/* Die Zeile „Glomer.com / Buchhandlung Engel … Bestellung G-…"
              steht in der Liquid-Vorlage, erscheint im Order-Printer-Druck
              aber nicht (Vergleich mit G-10181, 28.10.2026). Für ein
              gleiches Bild fehlt sie hier auch. */}
          <thead>
            <tr>
              <th className="ls-spalte-bild" />
              <th>Artikel</th>
              <th className="ls-spalte-menge">Anzahl</th>
            </tr>
          </thead>
          <tbody>
            {d.abschnitte.map((a, i) => (
              <Abschnitt key={i} abschnitt={a} />
            ))}
          </tbody>
        </table>
      )}
      <hr className="ls-linie" />

      {d.kopf.anmerkung && (
        <div className="ls-anmerkung">
          <p className="ls-untertitel">Anmerkungen</p>
          <p className="ls-anmerkung-text">{d.kopf.anmerkung}</p>
        </div>
      )}

      <div className="ls-fuss">
        <p>Danke, dass Sie bei uns eingekauft haben!</p>
        <p>
          <strong>{d.shop.name} / Buchhandlung Engel</strong>
          <br />
          E&amp;V GmbH • Alexanderstr. 11 • 70184 Stuttgart
          {d.shop.email && (
            <>
              <br />
              {d.shop.email}
            </>
          )}
          {d.shop.domain && (
            <>
              <br />
              {d.shop.domain}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function Zeilen({ zeilen }: { zeilen: string[] }) {
  return (
    <>
      {zeilen.map((z, i) => (
        <span key={i}>
          {i > 0 && <br />}
          {z}
        </span>
      ))}
    </>
  );
}

function Abschnitt({ abschnitt: a }: { abschnitt: LieferscheinAbschnitt }) {
  return (
    <>
      {a.titel !== null && (
        <tr>
          <td colSpan={3} className={a.art === 'unter' ? 'ls-unterzeile' : 'ls-gruppenzeile'}>
            {a.titel}
          </td>
        </tr>
      )}
      {a.zeilen.map((z, i) => (
        <Zeile key={i} zeile={z} />
      ))}
    </>
  );
}

function Zeile({ zeile: z }: { zeile: LieferscheinZeile }) {
  return (
    <tr className="ls-position">
      <td className="ls-spalte-bild">
        {z.bild && (
          <div className="ls-bild">
            {/* Shopify-CDN liefert auf Wunsch kleiner aus; 300 px reichen
                für 58 px Anzeige bei Druckauflösung, wie in der Vorlage. */}
            <img src={`${z.bild}${z.bild.includes('?') ? '&' : '?'}width=300`} alt="" />
          </div>
        )}
      </td>
      <td className="ls-beschreibung">
        <div>{z.titel}</div>
        {z.autor && <div>{z.autor}</div>}
        {z.variante && <div>{z.variante}</div>}
        {z.sku && <div>{z.sku}</div>}
      </td>
      <td className="ls-spalte-menge">{z.menge}</td>
    </tr>
  );
}

